/**
 * 日記頁（第 2 個 tab）—— ⭐ **純閱讀**（一頁接一頁、照建立日期、似揭書）
 *
 * 產品負責人 2026-09-24：「this diary page is only to read a diary, one by one, in according to date
 *   created… see if the presentation is more like reading a diary. from visually and the flipping action,
 *   so be more obvious」
 *   → 呢頁**冇**分類／**冇** switch／**冇**曲風 tag／**冇**順住播（分類同順播搬去**播放器頁**）
 *
 * 結構（`P-90` 拆元件；每個檔 ≤200 行）：
 *   · 呢個檔 ＝ 頁面骨架（頂欄／空狀態／揭頁容器／分享彈窗）
 *   · `components/DiaryCard.tsx` ＝ 一頁日記；`JournalEditor.tsx` ＝ 自由書寫；`hooks/useJournalAutosave.ts` ＝ 自動儲存
 * ⚠️ 只收 `succeeded`（有音頻）嘅歌；次序 ＝ **建立日期（舊 → 新）**（`P-141`）
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EmptyState from '@coeldery/empty-state';
import PageFlip, { pageLabel, stepPage } from '@coeldery/page-flip';
import ScreenTopBar from '@coeldery/screen-top-bar';
import DiaryCard from '../components/DiaryCard';
import SegmentedSwitch from '@coeldery/segmented-switch';
import DiaryList from '../features/diary/DiaryList';
import AddToPlaylist from '../components/AddToPlaylist';
import DeviceRestore from '../components/DeviceRestore';
import { useJournalAutosave } from '../hooks/useJournalAutosave';
import { useSongActions } from '../hooks/useSongActions';
import { useLibrary } from '../hooks/useLibrary';
import { tracksFromSongs } from '../player/playerModel';
import { usePlayer } from '../player/PlayerContext';
import SongTitleRename from '../components/SongTitleRename';

/** ⭐ UI v2 步 10（設計 S8）：頂欄翻頁掣已搬去底部 `.flipnav`（見下面 render） */

export default function SongsPage() {
  const { t, i18n } = useTranslation();
  const player = usePlayer();
  const { deviceId, songs, setSongs, hydrated, error } = useLibrary();
  const [index, setIndex] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  /** ⭐ 0013（`decisions/033`）：要加入播放清單嘅歌（`null` ＝ 彈窗閂住） */
  const [playlistSongId, setPlaylistSongId] = useState<string | null>(null);
  /** ⭐ 0016（`decisions/039`）：要改歌名嘅歌（`null` ＝ 彈窗閂住） */
  const [renameSongId, setRenameSongId] = useState<string | null>(null);
  /** ⭐ UI v2 步 10（設計 S8）：**日記／清單** 兩種睇法 */
  const [mode, setMode] = useState<'diary' | 'list'>('diary');

  /**
   * 日記只收有音頻嘅歌；次序 ＝ **新 → 舊**（第一頁 ＝ **最新**歌，越揭越舊）。
   * ⚠️ **2026-09-26（產品負責人）**：「the first page should be the latest song, then the next page will be
   *   the one wrote before…」⇒ **推翻 `P-141`「舊 → 新」** ✗；同 `/song/list`（`created_at DESC`）一致。
   * ⚠️ 連帶：日記頁**播放次序**跟畫面（由最新開始播）。
   */
  const entries = useMemo(
    () =>
      songs
        .filter((s) => s.status === 'succeeded' && Boolean(s.audioKey))
        .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''))),
    [songs],
  );
  /** 順播 queue（撳日記頁播放器時用；同畫面次序一致） */
  const queue = useMemo(() => tracksFromSongs(entries, { deviceId }), [deviceId, entries]);
  /** 自由書寫自動儲存（`hooks/useJournalAutosave.ts`；失敗 → `actionError` 照實講） */
  const journal = useJournalAutosave(entries, { deviceId, onError: setActionError });
  /** 揀版本／改歌名（`hooks/useSongActions.ts`；成功 merge 落 library ＋ 更新「而家播緊」嗰條；失敗照實講） */
  const actions = useSongActions({
    deviceId,
    onUpdated: (updated) => {
      setSongs((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
      /** ⚠️ 正在播嗰首 → 底部常駐播放條／鎖屏（MediaSession）即刻跟（`Track.title` 係播放時嘅 snapshot） */
      player.renameTrack(updated.id, updated.title);
    },
    onError: setActionError,
  });
  const renameTarget = entries.find((s) => s.id === renameSongId) ?? null;

  /** 逐首歌砌一頁日記（`DiaryCard`；大日期＋頁碼 → 歌名 → 自由書寫 → 播放器 → 歌詞 → 分享） */
  const pages = entries.map((s, pageIndex) => ({
    key: s.id,
    node: (
      <DiaryCard
        song={s}
        index={pageIndex}
        total={entries.length}
        locale={i18n.language}
        queue={queue}
        deviceId={deviceId}
        draft={journal.drafts[s.id] ?? ''}
        saveState={journal.saveState[s.id]}
        onJournalChange={(text) => journal.onChange(s.id, text)}
        onRetry={() => void journal.save(s.id, journal.drafts[s.id] ?? '')}
        busy={actions.busyId === s.id}
        onPick={(i) => void actions.pickVariant(s.id, i)}
        onAddPlaylist={() => setPlaylistSongId(s.id)}
        /** ⭐ 0016（`decisions/039`）：改歌名（彈窗喺呢頁；請求由 `useSongActions` 做） */
        onRenameTitle={() => setRenameSongId(s.id)}
      />
    ),
  }));

  return (
    <div className="page">
      <ScreenTopBar title={t('diary_title')} />
      <div className="page__scroll" style={{ padding: '12px' }}>
        {!hydrated ? (
          <p className="page__loading">{t('chat_thinking')}</p>
        ) : error ? (
          <p className="page__error" role="alert">
            {t('songs_list_failed')}（{error}）
          </p>
        ) : (
          <>
            {actionError ? (
              <p className="page__error" role="alert" style={{ marginBottom: '10px' }}>
                {actionError}
              </p>
            ) : null}
            {/* ⭐ UI v2 步 10（設計 S8）：**日記／清單** 兩種睇法（`.diary-roll__bar`） */}
            <div className="diary-roll__bar" style={{ marginBottom: '10px' }}>
              <SegmentedSwitch
                labelKey="diary_mode_switch_aria"
                value={mode}
                onChange={(id) => setMode(id === 'list' ? 'list' : 'diary')}
                options={[
                  { id: 'diary', label: t('diary_mode_diary') },
                  { id: 'list', label: t('diary_mode_list') },
                ]}
              />
            </div>
            {entries.length === 0 ? (
              <>
                <EmptyState titleKey="diary_empty_title" bodyKey="diary_empty_body" />
                {/* ⭐ 2026-09-23#3（B2）：**空狀態就要見到恢復入口**（iPhone 主畫面 app 同 Safari 分開儲存） */}
                <DeviceRestore compact />
              </>
            ) : mode === 'list' ? (
              <DiaryList entries={entries} locale={String(i18n.language)} onOpen={setIndex} />
            ) : (
              <>
                <PageFlip
                  pages={pages}
                  index={Math.min(index, pages.length - 1)}
                  onIndexChange={setIndex}
                  labelKey="diary_page_aria"
                  /** ⭐ 翻頁掣喺底部 `.flipnav` → 模組唔出自己嗰套（滑動／鍵盤照樣有效） */
                  chrome={false}
                />
                <p data-testid="diary-page-label" className="flipnav__of" style={{ display: 'block', textAlign: 'center', margin: '10px 0 0' }}>
                  {pageLabel(Math.min(index, entries.length - 1), entries.length)}
                </p>
              </>
            )}
          </>
        )}
      </div>

      {/* ⭐ UI v2 步 10（設計 S8）：揭頁導航 `.flipnav`（左＝上一頁、右＝下一頁） */}
      {hydrated && !error && entries.length > 1 ? (
        <div className="flipnav">
          <button
            type="button"
            data-testid="diary-prev"
            className="flipnav__btn"
            disabled={index <= 0}
            onClick={() => setIndex((i) => stepPage(entries.length, i, -1))}
          >
            {t('flip_hint_prev')}
          </button>
          <span className="flipnav__of">{t('diary_page_of', { n: Math.min(index, entries.length - 1) + 1, total: entries.length })}</span>
          <button
            type="button"
            data-testid="diary-next"
            className="flipnav__btn"
            disabled={index >= entries.length - 1}
            onClick={() => setIndex((i) => stepPage(entries.length, i, 1))}
          >
            {t('flip_hint_next')}
          </button>
        </div>
      ) : null}

      {/* ⭐ 0016（`decisions/039`）：改歌名（日記頁歌名旁嘅 ✎） */}
      <SongTitleRename
        song={renameTarget}
        busy={actions.busyId === renameTarget?.id}
        onSave={actions.renameTitle}
        onClose={() => setRenameSongId(null)}
      />

      {/* ⭐ 0013（`decisions/033`）：〔加入播放清單〕彈窗（對話頁同一個元件） */}
      <AddToPlaylist
        open={playlistSongId !== null}
        songId={playlistSongId}
        onClose={() => setPlaylistSongId(null)}
      />
    </div>
  );
}

